// @ts-check
import PlayerMarketModel from "./player-market.model";
import PlayerOutcomeModel from "./player-outcome.model";
import PlayerTabGroupModel from "./player-tab-group.model";

export default class PlayerTabModel {
  /**
   * 
   * @param {import('./player.api').TPlayerDetails} data 
   */
  constructor(data) {
    const pmd = data.pmd || {
      p: {},
      pmg: [],
      pmv: [],
    };
    /**
     * Map of player key -> player info
     */
    const playerMap = pmd.p || {};
    /**
     * @type {import("./player.api").TPlayerMarketGroupMap}}
     */
    const marketGroupMap = pmd.pmg.reduce((acc, g) => {
      acc[g.gid] = g;
      acc[g.gid].playerListMap = {};
      return acc;
    }, {});

    /**
     * @type {{[marketId:number]:import('./player.api').TPlayerMarketCustomView}}
     */
    const customMarketView = pmd.pmv.reduce((acc, g) => {
      acc[g.ot] = g;
      return acc;
    }, {});

    const rawMarketList = data.t[0] && data.t[0].o || [];

    /**
     * Tab name
     * @type {string}
     */
    this.name = data.t[0] && data.t[0].n || '';

    /**
     * List of market groups
     * @type {PlayerTabGroupModel[]}
     */
    this.groups = rawMarketList.reduce((groups, rawMarket) => {
      const customView = customMarketView[rawMarket.id];
      const sortByTeam = (customView && customView.st) || false;
      // Check if market is in grouped markets
      if (customView && customView.gid) {
        const gid = customView.gid;
        if (!marketGroupMap[gid].group) {
          marketGroupMap[gid].group = new PlayerTabGroupModel(marketGroupMap[gid].gn);
          groups.push(marketGroupMap[gid].group);
        }
        if (sortByTeam) {
          marketGroupMap[gid].sortByTeam = true;
        }
        const { m, ...mr } = rawMarket;
        const market = new PlayerMarketModel(mr, playerMap);

        if (market.cashout) {
          marketGroupMap[gid].group.hasCashout = true;
        }

        if (customView.co === marketGroupMap[gid].sc) {
          marketGroupMap[gid].sortMarketId = market.marketId;
        }
        marketGroupMap[gid].group.groupedMarkets.push(market);
        m.forEach(o => {
          const key = o.n.split(' ').find(k => playerMap[k]);
          // Player was found in player map
          if (key) {
            const playerOutcome = new PlayerOutcomeModel(o, playerMap);
            // If player doesn't exist create new playerList object
            // And add it to the playerList and playerListMap
            if (!marketGroupMap[gid].playerListMap[key]) {
              marketGroupMap[gid].playerListMap[key] = {
                name: playerMap[key].n,
                teamEnum: playerMap[key].t,
                markets: {},
                outcomes: {},
              };
              marketGroupMap[gid].group.playersList.push(marketGroupMap[gid].playerListMap[key]);
            }
            // If outcome has sbv add this propery to the market as well
            // So we can draw extra colmun for SBV values
            if (playerOutcome.sbv) {
              market.hasSbv = true;
            }
            // Finally add the outcome to markets object of the playerList object
            marketGroupMap[gid].playerListMap[key].markets[market.marketId] = playerOutcome;
          }
        });

        return groups;
      }

      // Check if market has grouped outcomes
      if (customView && customView.oc) {
        const { m, ...mr } = rawMarket;
        const market = new PlayerMarketModel(mr);
        const playerTabGroup = new PlayerTabGroupModel(market.marketName);
        if (market.cashoutAvailable) {
          playerTabGroup.hasCashout = true;
        }
        playerTabGroup.groupedOutcomes = customView.oc.map((oc) => {
          return {
            name: oc.cn,
            id: oc.ocn,
            column: oc.co,
          };
        });
        playerTabGroup.groupedOutcomes.sort((a, b) => a.column - b.column);
        /**
         * @type {{[playerKey:string]: import('./player-tab-group.model').PlayerListModel}}
         */
        const playerListMap = {};
        playerTabGroup.complexMarket = market;
        groups.push(playerTabGroup);
        m.forEach(o => {
          const key = o.n.split(' ').find(k => playerMap[k]);
          if (key) {
            const playerOutcome = new PlayerOutcomeModel(o, playerMap);
            if (!playerListMap[key]) {
              playerListMap[key] = {
                name: playerMap[key].n,
                teamEnum: playerMap[key].t,
                markets: {},
                outcomes: {},
                sbv: '',
              };
              playerTabGroup.playersList.push(playerListMap[key]);
            }
            if (playerOutcome.sbv && playerListMap[key].sbv && playerOutcome.sbv !== playerListMap[key].sbv) {
              if (!playerListMap[key].outcomes[playerOutcome.outcomeIdentifier]) {
                return;
              }
              return;
            }
            // If outcome has sbv add this propery to the market as well
            // So we can draw extra colmun for SBV values
            if (playerOutcome.sbv) {
              market.hasSbv = true;
              playerListMap[key].sbv = playerOutcome.sbv;
            }
            // TODO: how to handle multiple SBV for same outcomeIdenfifier and player?
            playerListMap[key].outcomes[playerOutcome.outcomeIdentifier] = playerOutcome;
          }
        });
        // Check if sorting column is present for grouped outcomes
        const outcomeKey = playerTabGroup.groupedOutcomes.find(go => go.column === customView.co);
        // If present sort by outcome value of the column
        if (outcomeKey) {
          const outcomeId = outcomeKey.id;
          playerTabGroup.playersList.sort((a, b) => {
            const at = (sortByTeam && a.teamEnum) || 0;
            const bt = (sortByTeam && b.teamEnum) || 0;
            if (at - bt === 0) {
              const as = (a.outcomes && a.outcomes[outcomeId] && +a.outcomes[outcomeId].outcome) || 0;
              const bs = (b.outcomes && b.outcomes[outcomeId] && +b.outcomes[outcomeId].outcome) || 0;
              return as - bs;
            }
            return at - bt;
          });
        }

        return groups;
      }

      const market = new PlayerMarketModel(rawMarket, playerMap);
      const playerTabGroup = new PlayerTabGroupModel(market.marketName);
      if (market.cashout) {
        playerTabGroup.hasCashout = true;
      }
      if (customView && customView.ol) {
        market.outcomeLayout = customView.ol;
      }
      playerTabGroup.simpleMarkets.push(market);
      groups.push(playerTabGroup);
      return groups;
    }, []);

    // Finally sort the market groups
    Object.values(marketGroupMap).forEach(/** @param {import('./player.api').TPlayerMarketGroupMapVal} mg */(mg) => {
      mg.group.groupedMarkets.sort((a, b) => {
        const as = (customMarketView[a.marketId] && customMarketView[a.marketId].co) || 0;
        const bs = (customMarketView[b.marketId] && customMarketView[b.marketId].co) || 0;
        return as - bs;
      });
      const marketKey = mg.sortMarketId;
      if (marketKey) {
        mg.group.playersList.sort((a, b) => {
          const at = (mg.sortByTeam && a.teamEnum) || 0;
          const bt = (mg.sortByTeam && b.teamEnum) || 0;
          if (at - bt === 0) {
            const as = (a.markets[marketKey] && +a.markets[marketKey].outcome) || 0;
            const bs = (b.markets[marketKey] && +b.markets[marketKey].outcome) || 0;
            return as - bs;
          }
          return at - bt;
        });
      }
    });
  }
}